import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { parseCurrency, applyCurrencyMask } from '../../utils/currency.ts';
import api from '../../services/api.ts';
import FormPage from '../../components/ui/FormPage.tsx';
import MoreOptions from '../../components/ui/MoreOptions.tsx';
import { payableDetailPath, payableListPath } from '../../utils/payable.ts';
import { todayDateOnly } from '../../utils/date.ts';

/**
 * Formulário dedicado de Conta a pagar (FormPage).
 * Sem copy de “dívida” / “saldo devedor”.
 */
const CreatePayable: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const backPath = payableListPath();

  const [creditorName, setCreditorName] = useState('');
  const [description, setDescription] = useState('');
  const [amountDisplay, setAmountDisplay] = useState('');
  const [amount, setAmount] = useState(0);
  const [interestRate, setInterestRate] = useState(0);
  const [dueDate, setDueDate] = useState(todayDateOnly());
  const [status, setStatus] = useState<'active' | 'paid' | 'overdue' | 'renegotiated'>('active');
  const [category, setCategory] = useState<'account_payable' | 'other'>('account_payable');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation(
    async () => {
      const payload = {
        creditorName,
        description,
        originalAmount: amount,
        currentAmount: amount,
        interestRate,
        dueDate,
        status,
        category,
        paymentMethod: paymentMethod || undefined,
        installments: 1,
        currentInstallment: 1,
      };
      const res = await api.post('/financeiro/contas-a-pagar', payload);
      return res.data;
    },
    {
      onSuccess: (created: any) => {
        queryClient.invalidateQueries('debts');
        queryClient.invalidateQueries('contas-a-pagar');
        queryClient.invalidateQueries('financeiro-dividas');
        const newId = created?.id;
        navigate(newId ? payableDetailPath(newId) : backPath);
      },
      onError: () => {
        setErrors({ submit: 'Erro ao criar conta a pagar. Tente novamente.' });
      },
    }
  );

  const validate = () => {
    const next: Record<string, string> = {};
    if (!description.trim()) next.description = 'Informe a descrição da conta.';
    if (!creditorName.trim()) next.creditorName = 'Informe o fornecedor / credor.';
    if (!amount || amount <= 0) next.amount = 'Informe um valor maior que zero.';
    if (interestRate < 0 || interestRate > 100) {
      next.interestRate = 'Taxa de juros deve estar entre 0% e 100%.';
    }
    if (!dueDate) next.dueDate = 'Informe a data de vencimento.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) mutation.mutate();
  };

  return (
    <FormPage
      backTo={backPath}
      backLabel="Voltar às contas a pagar"
      breadcrumb="Financeiro / Contas / A pagar / Nova"
      title="Nova conta a pagar"
      subtitle="Obrigação em aberto — ainda não saiu do caixa"
      hint="Campos essenciais primeiro. Use “Mais opções” só se precisar."
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={() => navigate(backPath)}>
            Cancelar
          </button>
          <button
            type="submit"
            form="create-payable-form"
            className="btn btn-primary"
            disabled={mutation.isLoading}
            data-form="create-payable"
          >
            {mutation.isLoading ? 'Salvando…' : 'Salvar conta a pagar'}
          </button>
        </>
      }
    >
      <form id="create-payable-form" noValidate onSubmit={onSubmit} data-form="create-payable">
        {errors.submit && <div className="field-error" style={{ marginBottom: 12 }}>{errors.submit}</div>}
        <div className="form-section-title">O essencial</div>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="description">Descrição *</label>
            <input
              id="description"
              className={`input${errors.description ? ' error' : ''}`}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Energia, escola, fornecedor"
            />
            {errors.description && <div className="field-error">{errors.description}</div>}
          </div>
          <div className="field">
            <label htmlFor="creditorName">Fornecedor / credor *</label>
            <input
              id="creditorName"
              className={`input${errors.creditorName ? ' error' : ''}`}
              value={creditorName}
              onChange={e => setCreditorName(e.target.value)}
              placeholder="Ex: Enel, Escola XYZ"
            />
            {errors.creditorName && <div className="field-error">{errors.creditorName}</div>}
          </div>
          <div className="field">
            <label htmlFor="amount">Valor *</label>
            <input
              id="amount"
              className={`input${errors.amount ? ' error' : ''}`}
              value={amountDisplay}
              onChange={e => {
                const masked = applyCurrencyMask(e.target.value);
                setAmountDisplay(masked);
                setAmount(parseCurrency(masked));
              }}
              placeholder="R$ 0,00"
            />
            {errors.amount && <div className="field-error">{errors.amount}</div>}
          </div>
          <div className="field">
            <label htmlFor="dueDate">Vencimento *</label>
            <input
              id="dueDate"
              type="date"
              className={`input${errors.dueDate ? ' error' : ''}`}
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
            />
            {errors.dueDate && <div className="field-error">{errors.dueDate}</div>}
          </div>
        </div>

        <MoreOptions>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="category">Categoria</label>
              <select
                id="category"
                className="select"
                value={category}
                onChange={e => setCategory(e.target.value as typeof category)}
              >
                <option value="account_payable">Conta a pagar</option>
                <option value="other">Outros</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                className="select"
                value={status}
                onChange={e => setStatus(e.target.value as typeof status)}
              >
                <option value="active">Em aberto</option>
                <option value="overdue">Vencida</option>
                <option value="renegotiated">Renegociada</option>
                <option value="paid">Paga</option>
              </select>
            </div>
            <div className="field">
              <label htmlFor="interestRate">Juros (% a.m.)</label>
              <input
                id="interestRate"
                type="number"
                inputMode="decimal"
                step="0.01"
                className={`input${errors.interestRate ? ' error' : ''}`}
                value={interestRate}
                onChange={e => setInterestRate(Number(e.target.value) || 0)}
              />
              {errors.interestRate && <div className="field-error">{errors.interestRate}</div>}
            </div>
            <div className="field">
              <label htmlFor="paymentMethod">Forma preferida</label>
              <input
                id="paymentMethod"
                className="input"
                value={paymentMethod}
                onChange={e => setPaymentMethod(e.target.value)}
                placeholder="PIX, boleto…"
              />
            </div>
          </div>
        </MoreOptions>
      </form>
    </FormPage>
  );
};

export default CreatePayable;
